import React, { useEffect, useMemo, useRef, useState } from "react";

import { Typography, IconButton } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useAppState } from "Context/StateContext";
import { Trash as IconTrash } from "iconsax-react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import ReactTimeAgo from "react-time-ago";

import { Comment } from "../../../../../models/comment";
import useStyles from "./styles";

export interface Props {
  comment: Comment;
  onDeleteComment?: (data: Comment) => void;
  hasPermission?: boolean;
}

const CommentHistoryItem = ({ comment, onDeleteComment, hasPermission = true }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const messageRef = useRef<HTMLParagraphElement>(null);

  const [clamp, setClamp] = useState<boolean>(true);

  const { createdAt, message, user } = comment;

  const getUserAvatar = (): string => {
    // eslint-disable-next-line global-require
    return user?.picture?.mediaLink || require("Assets/profile.png");
  };

  const isOwnComment = useMemo(
    () => comment?.userId && currentUser?.id && comment.userId === currentUser.id,
    [comment, currentUser]
  );

  // const showMoreVert = useMemo(() => currentUser?.id === user?.id, [currentUser, user]);

  const handleReadMore = () => {
    setClamp(false);
  };

  useEffect(() => {
    const checkClamps = () => {
      const { current: msgElement } = messageRef;

      if (!msgElement) return;

      const lastClampValue = msgElement.style.webkitLineClamp;

      msgElement.style.webkitLineClamp = "3";
      setClamp(msgElement.clientHeight !== msgElement.scrollHeight);
      msgElement.style.webkitLineClamp = lastClampValue;
    };

    const debouncedCheck = debounce(checkClamps, 50);

    checkClamps();

    window.addEventListener("resize", debouncedCheck);

    return () => window.removeEventListener("resize", debouncedCheck);
  }, [messageRef]);

  return (
    <div className={classes.container}>
      <div className={classes.avatarArea}>
        <img className={classes.userAvatar} src={getUserAvatar()} />
      </div>
      <div className={classes.content}>
        <div className={classes.topCard}>
          <div className={classes.topCardHeader}>
            <Typography className={classes.userName}>{user?.fullName}</Typography>
            <Typography className={classes.timestamp}>
              {/* {createdAt} */}
              {!!createdAt && <ReactTimeAgo date={new Date(createdAt)} />}
            </Typography>
          </div>

          {!!(isOwnComment && onDeleteComment) && hasPermission && (
            <div className={classes.deleteButton}>
              <IconButton onClick={() => onDeleteComment(comment)}>
                <IconTrash size={16} color={colors.gray800} />
              </IconButton>
            </div>
          )}

          {/* TO DO: feature to edit and delete own user comments is planned for future sprints. Jandson S. Nunes 2024.06.11 */}
          {/* <div className={classes.btnArea}>
            {showMoreVert && (
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            )}
          </div> */}
        </div>
        <div className={classes.paragraph}>
          <Typography
            className={`${classes.message} ${clamp ? classes.clamp : ""}`}
            ref={messageRef}
          >
            {message}
          </Typography>
          {clamp && (
            <Typography className={classes.readMore} onClick={handleReadMore}>
              {translate("readMore")}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentHistoryItem;
